/**
 * The gallery grid — one neon card per game. Clicking the card face
 * launches; the TOP 10 chip opens the leaderboard modal.
 */

import { CATALOG, type CatalogEntry } from '../catalog.js';
import { track } from '../runtime/analytics.js';
import { openLeaderboard } from './leaderboard.js';
import { launchGame, type PlaySession } from './play.js';
import { resolvePlayAccess } from './play-access.js';
import { consumePendingLaunch, showLoginWall, showPaywallStub } from './play-gate-wall.js';
import { playCountFor } from './plays.js';

let activeSession: PlaySession | null = null;

const NEW_ROW_SIZE = 3;

/**
 * Gallery order: the 3 newest titles hold the front row (by `released`
 * date), then everything else ranks by play count — GLOBAL counts when
 * the API has ever answered, this device's ledger otherwise (plays.ts).
 * Ties fall back to the classic marquee lineup (catalog order).
 */
function orderedCatalog(): { entry: CatalogEntry; isNew: boolean }[] {
  const byLineup = new Map(CATALOG.map((e, i) => [e.id, i]));
  const newest = [...CATALOG]
    .sort(
      (a, b) =>
        b.released.localeCompare(a.released) ||
        (byLineup.get(a.id) ?? 0) - (byLineup.get(b.id) ?? 0)
    )
    .slice(0, NEW_ROW_SIZE);
  const newIds = new Set(newest.map((e) => e.id));
  const rest = CATALOG.filter((e) => !newIds.has(e.id)).sort(
    (a, b) =>
      playCountFor(b.id) - playCountFor(a.id) ||
      (byLineup.get(a.id) ?? 0) - (byLineup.get(b.id) ?? 0)
  );
  return [
    ...newest.map((entry) => ({ entry, isNew: true })),
    ...rest.map((entry) => ({ entry, isNew: false })),
  ];
}

export function renderGrid(): void {
  const grid = document.getElementById('gallery-grid');
  if (!(grid instanceof HTMLElement)) {
    throw new Error('gallery grid DOM missing — index.html is broken');
  }
  grid.textContent = '';

  orderedCatalog().forEach(({ entry, isNew }, i) => {
    grid.appendChild(buildCard(entry, i, isNew));
  });
}

function buildCard(entry: CatalogEntry, index: number, isNew: boolean): HTMLElement {
  const card = document.createElement('article');
  card.className = 'card';
  card.style.setProperty('--card-accent', entry.accent);
  card.style.setProperty('--card-accent2', entry.accent2);
  card.style.setProperty('--card-i', String(index));

  const band = document.createElement('div');
  band.className = 'card__band';

  if (isNew) {
    const chip = document.createElement('span');
    chip.className = 'card__new';
    chip.textContent = 'NEW';
    card.appendChild(chip);
  }

  // the cabinet screen — a CSS mini-scene unique to each game
  const scene = document.createElement('div');
  scene.className = 'card__scene';
  scene.dataset.scene = entry.id;
  for (let s = 1; s <= 3; s++) {
    const el = document.createElement('i');
    el.className = `sc sc${s}`;
    scene.appendChild(el);
  }
  const glare = document.createElement('span');
  glare.className = 'card__glare';
  scene.appendChild(glare);

  const title = document.createElement('h2');
  title.className = 'card__title';
  title.textContent = entry.title;

  const genre = document.createElement('span');
  genre.className = 'card__genre';
  genre.textContent = `${entry.genre} · 1984`;

  const blurb = document.createElement('p');
  blurb.className = 'card__blurb';
  blurb.textContent = entry.blurb;

  const actions = document.createElement('div');
  actions.className = 'card__actions';

  const playBtn = document.createElement('button');
  playBtn.type = 'button';
  playBtn.className = 'card__play';
  playBtn.textContent = '▶ INSERT COIN';
  playBtn.addEventListener('click', () => launch(entry));

  actions.appendChild(playBtn);

  if (!entry.scoreless) {
    const boardBtn = document.createElement('button');
    boardBtn.type = 'button';
    boardBtn.className = 'card__board';
    boardBtn.textContent = 'TOP 10';
    boardBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      openLeaderboard(entry);
    });
    actions.appendChild(boardBtn);
  }

  card.append(band, scene, title, genre, blurb, actions);
  return card;
}

function launch(entry: CatalogEntry): void {
  if (activeSession) return; // one cartridge at a time — arcade rules
  track('cartridge_selected', { cartridgeId: entry.id, remastered: true });
  void gateThenLaunch(entry);
}

/**
 * The entry gate (sc-6940): resolve play access, THEN mount — or show the
 * login wall / paywall. A signed-in token play spends the token here
 * (server-authoritative); the free run and anon-taste paths spend
 * nothing and let play.ts's `started` handler tick the device meter.
 */
async function gateThenLaunch(entry: CatalogEntry): Promise<void> {
  const verdict = await resolvePlayAccess();
  switch (verdict.grant) {
    case 'play':
      startSession(entry);
      break;
    case 'login':
      track('login_wall_shown', { cartridgeId: entry.id });
      void showLoginWall(entry.id, entry.title);
      break;
    case 'paywall':
      track('paywall_shown', { cartridgeId: entry.id });
      void showPaywallStub(verdict.resetsAtUtc);
      break;
    case 'error':
      // Server said no (fail-closed) — never a free run. Treat as paywall
      // so the player sees "try again shortly" rather than a silent nothing.
      void showPaywallStub();
      break;
  }
}

function startSession(entry: CatalogEntry): void {
  if (activeSession) return;
  activeSession = launchGame(entry, {
    onClose: () => {
      activeSession = null;
      // play counts changed — let the lineup re-rank while the player
      // is looking at the gallery anyway
      renderGrid();
    },
  });
}

/**
 * Auto-relaunch after an auth round-trip (sc-6940). main.ts calls this on
 * boot when the callback signed the player in and a pending cartridge was
 * stashed before the redirect. The player lands back mid-flow, in the game
 * they were trying to coin.
 */
export function relaunchPending(): void {
  const id = consumePendingLaunch();
  if (!id) return;
  const entry = CATALOG.find((c) => c.id === id);
  if (entry) void gateThenLaunch(entry);
}
